import React, { useState, useEffect } from 'react';
import { scheduleAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar, Clock, Loader2, Plus, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const min = m.toString().padStart(2, '0');
    TIME_OPTIONS.push(`${hour}:${min}`);
  }
}

const Schedule = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSchedule();
  }, []);

  const loadSchedule = async () => {
    try {
      const response = await scheduleAPI.getSchedule();
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSlot = () => {
    setSlots([...slots, { day_of_week: 0, start_time: '09:00', end_time: '17:00' }]);
    setHasChanges(true);
  };

  const removeSlot = (index) => {
    setSlots(slots.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const updateSlot = (index, field, value) => {
    const newSlots = [...slots];
    newSlots[index] = { ...newSlots[index], [field]: field === 'day_of_week' ? parseInt(value) : value };
    setSlots(newSlots);
    setHasChanges(true);
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await scheduleAPI.setSchedule(slots, 'America/New_York');
      toast.success('Schedule saved');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const clearSchedule = async () => {
    if (!window.confirm('Clear your entire schedule?')) return;
    
    try {
      await scheduleAPI.clearSchedule();
      setSlots([]);
      toast.success('Schedule cleared');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to clear schedule');
    }
  };

  // Group slots by day for display
  const slotsByDay = DAYS.map((day, index) => ({
    day,
    dayIndex: index,
    slots: slots.filter(s => s.day_of_week === index)
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="schedule-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight">
              My Schedule
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Set your availability to receive ride requests
            </p>
          </div>
          <div className="flex gap-3">
            {slots.length > 0 && (
              <Button variant="outline" onClick={clearSchedule}>
                Clear All
              </Button>
            )}
            <Button onClick={addSlot} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Slot
            </Button>
          </div>
        </div>

        {/* Schedule Editor */}
        <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-600" />
              Weekly Availability
            </CardTitle>
          </CardHeader>
          <CardContent>
            {slots.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  No schedule set. Add time slots when you're available to drive.
                </p>
                <Button onClick={addSlot} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Slot
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {slots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                  >
                    <select
                      value={slot.day_of_week}
                      onChange={(e) => updateSlot(index, 'day_of_week', e.target.value)}
                      className="flex-1 p-2 border rounded-lg bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                    >
                      {DAYS.map((day, i) => (
                        <option key={i} value={i}>{day}</option>
                      ))}
                    </select>
                    
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <select
                        value={slot.start_time}
                        onChange={(e) => updateSlot(index, 'start_time', e.target.value)}
                        className="p-2 border rounded-lg bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                      <span className="text-slate-400">to</span>
                      <select
                        value={slot.end_time}
                        onChange={(e) => updateSlot(index, 'end_time', e.target.value)}
                        className="p-2 border rounded-lg bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSlot(index)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {hasChanges && (
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={saveSchedule}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Save Schedule
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Weekly Overview */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">
              Weekly Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {slotsByDay.map(({ day, dayIndex, slots: daySlots }) => (
                <div
                  key={dayIndex}
                  className={`p-3 rounded-lg text-center ${
                    daySlots.length > 0
                      ? 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                      : 'bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <p className="font-semibold text-sm text-slate-900 dark:text-white">
                    {day.slice(0, 3)}
                  </p>
                  {daySlots.length > 0 ? (
                    <div className="mt-2 space-y-1">
                      {daySlots.map((slot, i) => (
                        <p key={i} className="text-xs text-green-700 dark:text-green-400">
                          {slot.start_time}-{slot.end_time}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mt-2">Off</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Schedule;
